import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Tab,
  Tabs,
  Typography,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
} from '@mui/material';
import {
  Edit as EditIcon,
  Event as EventIcon,
  Person as PersonIcon,
  LocalPhone as LocalPhoneIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Schedule as ScheduleIcon,
  School as SchoolIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNotification } from '../../contexts/NotificationContext';
import especialistaService from '../../services/especialistaService';
import citaService from '../../services/citaService';
import PageHeader from '../../components/common/PageHeader';
import AsignarUsuariosInternos from '../../components/especialistas/AsignarUsuariosInternos';
import { useAuth } from '../../hooks/useAuth';

// Componente de panel para las tabs
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const formatDate = (dateString) => {
  if (!dateString) return 'No registrado';
  try {
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: es });
  } catch (error) {
    return dateString;
  }
};

const EspecialistaDetail = () => {
  const { id } = useParams();
  const { showNotification } = useNotification();
  const { user } = useAuth(); // Añadir esta línea
  const [especialista, setEspecialista] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [proximasCitas, setProximasCitas] = useState([]);
  const [citasLoading, setCitasLoading] = useState(false);

  const isAdmin = user?.rol === 'Administrador';

  // Cargar datos del especialista
  useEffect(() => {
    const fetchEspecialista = async () => {
      setLoading(true);
      try {
        const response = await especialistaService.getEspecialista(id);
        setEspecialista(response.data);
      } catch (error) {
        console.error('Error fetching especialista:', error);
        showNotification(
          'Error al cargar los datos del especialista',
          'error'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchEspecialista();
  }, [id, showNotification]);

  // Cargar próximas citas cuando se selecciona esa pestaña
  useEffect(() => {
    if (tabValue === 1 && especialista) {
      const fetchCitas = async () => {
        setCitasLoading(true);
        try {
          // Obtener fecha actual en formato yyyy-MM-dd
          const hoy = new Date();
          const fechaInicio = format(hoy, 'yyyy-MM-dd');
          
          // Obtener fecha fin (1 mes después)
          const fechaFin = new Date(hoy);
          fechaFin.setMonth(hoy.getMonth() + 1);
          const fechaFinStr = format(fechaFin, 'yyyy-MM-dd');

          const response = await citaService.getCitas({
            page: 1,
            limit: 20,
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFinStr,
            especialista_id: especialista.id,
            estado: 'Programada,Confirmada'
          });
          
          setProximasCitas(response.data.citas || []);
        } catch (error) {
          console.error('Error fetching citas:', error);
          showNotification(
            'Error al cargar las citas',
            'error'
          );
        } finally {
          setCitasLoading(false);
        }
      };

      fetchCitas();
    }
  }, [tabValue, especialista, showNotification]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (!especialista) {
    return (
      <Box>
        <Typography variant="h5" color="error">
          Especialista no encontrado
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader 
        title={`${especialista.usuario?.nombre || ''} ${especialista.usuario?.apellidos || ''}`}
        subtitle="Datos del Especialista"
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Especialistas', to: '/especialistas' },
          { text: `${especialista.usuario?.nombre || ''} ${especialista.usuario?.apellidos || ''}` }
        ]}
        actionButton={
          <Box>
            <Button
              component={RouterLink}
              to={`/especialistas/editar/${especialista.id}`}
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              sx={{ mr: 1 }}
            >
              Editar
            </Button>
            <Button
              component={RouterLink}
              to={`/citas/nueva?especialista=${especialista.id}`}
              variant="contained"
              color="primary"
              startIcon={<EventIcon />}
            >
              Nueva Cita
            </Button>
          </Box>
        }
      />
      
      {/* Información básica y foto */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    DNI
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {especialista.dni}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Email
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {especialista.usuario?.email || 'No registrado'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Teléfono
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {especialista.telefono || 'No registrado'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    WhatsApp
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {especialista.whatsapp || 'No registrado'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Dirección
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {especialista.direccion || 'No registrada'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Fecha de Nacimiento
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDate(especialista.fecha_nacimiento)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Número de Colegiatura
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {especialista.num_colegiatura || 'No registrado'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Especialidades
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {especialista.especialidades?.length > 0 ? (
                      especialista.especialidades.map((especialidad) => (
                        <Chip
                          key={especialidad.id}
                          label={especialidad.nombre}
                          sx={{ 
                            backgroundColor: especialidad.color,
                            color: 'white'
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Sin especialidades asignadas
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar
                alt={`${especialista.usuario?.nombre || ''} ${especialista.usuario?.apellidos || ''}`}
                src={especialista.foto ? `${process.env.REACT_APP_API_URL}${especialista.foto}` : '/assets/images/default-user.png'}
                sx={{ width: 150, height: 150, mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                {especialista.usuario?.nombre || ''} {especialista.usuario?.apellidos || ''}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {especialista.especialidades?.map(e => e.nombre).join(', ')}
              </Typography>
              
              <Box mt={2} sx={{ width: '100%' }}>
                <Button
                  component={RouterLink}
                  to={`/citas/nueva?especialista=${especialista.id}`}
                  variant="contained"
                  color="primary"
                  startIcon={<EventIcon />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Agendar Cita
                </Button>
                <Button
                  component={RouterLink}
                  to={`/citas/calendario?especialista=${especialista.id}`}
                  variant="outlined"
                  color="primary"
                  startIcon={<ScheduleIcon />}
                  fullWidth
                >
                  Ver Agenda
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Pestañas con información detallada */}
      <Card>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="especialista tabs">
          <Tab label="Información Profesional" />
          <Tab label="Próximas Citas" />
          {isAdmin && <Tab label="Usuarios Internos" />} {/* Nueva pestaña solo visible para admins */}
        </Tabs>
      </Box>
        
        {/* Tab: Información Profesional */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {especialista.curriculum && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Curriculum
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" paragraph>
                  {especialista.curriculum}
                </Typography>
              </Grid>
            )}
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Información de Comisión
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1" paragraph>
                <strong>Porcentaje de Comisión:</strong> {especialista.porcentaje_comision}%
              </Typography>
            </Grid>
            
            {especialista.notas && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Notas Adicionales
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1">
                  {especialista.notas}
                </Typography>
              </Grid>
            )}
          </Grid>
        </TabPanel>
        
        {/* Tab: Próximas Citas */}
        <TabPanel value={tabValue} index={1}>
          {citasLoading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : proximasCitas.length === 0 ? (
            <Box textAlign="center" my={3}>
              <Typography variant="body1" color="textSecondary" gutterBottom>
                No hay citas programadas próximamente para este especialista.
              </Typography>
              <Button
                component={RouterLink}
                to={`/citas/nueva?especialista=${especialista.id}`}
                variant="contained"
                color="primary"
                startIcon={<EventIcon />}
                sx={{ mt: 2 }}
              >
                Agendar Cita
              </Button>
            </Box>
          ) : (
            <List>
              {proximasCitas.map((cita) => (
                <ListItemButton
                  key={cita.id}
                  component={RouterLink}
                  to={`/citas/${cita.id}`}
                  divider
                >
                  <ListItemIcon>
                    <EventNoteIcon color={cita.estado === 'Confirmada' ? 'success' : 'primary'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${formatDate(cita.fecha)} - ${cita.hora_inicio.substring(0, 5)} a ${cita.hora_fin.substring(0, 5)}`}
                    secondary={`Paciente: ${cita.paciente?.nombres} ${cita.paciente?.apellidos} | ${cita.especialidad?.nombre} | ${cita.estado}`}
                  />
                  <Chip
                    label={cita.estado}
                    color={cita.estado === 'Confirmada' ? 'success' : 'primary'}
                    size="small"
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </TabPanel>
        {isAdmin && (
          <TabPanel value={tabValue} index={2}>
            <AsignarUsuariosInternos 
              especialistaId={especialista.id} 
              nombreEspecialista={`${especialista.usuario?.nombre || ''} ${especialista.usuario?.apellidos || ''}`} 
            />
          </TabPanel>
        )}
      </Card>
 
    </Box>
  );
};

export default EspecialistaDetail;